import { useCart } from '@shopify/hydrogen-react';
import { CartCost, CartLine } from '@shopify/hydrogen-react/storefront-api-types';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CartBottom from './CartBottom';
import DiscountCode from './DiscountCode';
import Quantity from './Quantity';
import { formatCurrency } from '../../common/formatCurrencies';
import { NotificationMessages } from '../../common/notificationMessages';
import Button, { ButtonVariation } from '../../components/ui/Button';
import OptimizedImage from '../../components/ui/OptimizedImage';
import { getCartParams } from '../../config/enrichlyParams';
import { useStoreState } from '../../store';
import { elevarAddToCartFromCart, elevarRemoveFromCart, elevarViewCart } from '../../utils/elevar';
import { cartAttributesUpdate, removeCartItems } from '../../utils/storefront/cart/actions';
import { flatResponseEdges } from '../../utils/storefront/flatResponseEdges';
import { handleStorefrontClientError } from '../../utils/storefront/storefrontClientErrorHandler';

interface Props {
  lineItemClass?: string;
}

const CartContent: React.FC<Props> = ({ lineItemClass }) => {
  const navigate = useNavigate();
  const { id, cost, lines, linesRemove } = useCart();
  const { elevarCustomerData } = useStoreState(state => state.analytics);
  const orgContext = useStoreState(state => state.orgContext);
  const [cartLines, setCartLines] = useState<CartLine[]>([]);
  const [cartCost, setCartCost] = useState<CartCost>();
  const [disableCheckout, setDisableCheckout] = useState<boolean>(false);
  const [cartLoaded, setCartLoaded] = useState<boolean>(false);

  const removeItem = (cartId: string, lineItem: CartLine) => {
    setDisableCheckout(true);
    removeCartItems(cartId, [lineItem.id])
      .then(results => {
        linesRemove([lineItem.id]);
        results?.cart?.lines?.edges &&
          setCartLines(flatResponseEdges(results?.cart?.lines?.edges) as CartLine[]);
        results?.cart && setCartCost(results.cart.cost as CartCost);
        elevarRemoveFromCart(elevarCustomerData, lineItem, lineItem.quantity);
      })
      .catch(e => handleStorefrontClientError(e, NotificationMessages.DEFAULT_ERROR))
      .finally(() => setDisableCheckout(false));
  };

  const elevarHandleChange = (data, oldValue: number, newValue: number) => {
    if (newValue > oldValue) {
      elevarAddToCartFromCart(
        elevarCustomerData,
        data,
        `${window.location.origin}/product/${data.merchandise.product.handle}`
      );
    } else if (oldValue > newValue) {
      elevarRemoveFromCart(elevarCustomerData, data, 1);
    }
  };

  useEffect(() => {
    if (!lines?.length) {
      setCartLoaded(true);
      return;
    }
    setCartLoaded(true);
    setCartLines(lines as CartLine[]);
  }, [lines]);

  useEffect(() => {
    !!cost && setCartCost(cost as CartCost);
  }, [cost]);

  useEffect(() => {
    if (!id) return;
    const attrs = getCartParams()
      .map(p => ({
        key: p.cartAttribute as string,
        value: (orgContext as any)[p.contextKey] ?? '',
      }))
      .filter(a => a.value !== '');

    if (orgContext.orgId) {
      attrs.push({ key: 'organization', value: orgContext.orgId });
    }

    if (attrs.length) {
      cartAttributesUpdate(id, attrs);
    }
  }, [id, orgContext]);

  useEffect(() => {
    elevarViewCart(elevarCustomerData, lines as CartLine[], cost?.totalAmount?.amount);
  }, []);

  const goToProduct = (handle: string) => {
    navigate(`/product/${handle}`);
  };

  return (
    <div>
      {cartLoaded && !!cartLines?.length && (
        <div className="cart-list">
          {cartLines.map((item, key) => {
            return (
              <div
                key={key}
                className={classNames(
                  {
                    'cart-list-item': true,
                  },
                  lineItemClass
                )}
              >
                <div className="cart-list-item-image">
                  <OptimizedImage
                    url={item?.merchandise?.image?.url}
                    className="cart-list-item-image-inner cursor"
                    onClick={() => goToProduct(item?.merchandise.product.handle)}
                  />
                </div>
                <div className="cart-list-item-inner">
                  <div className="helper">
                    <div className="cart-list-item-meta">
                      <h6
                        className="heading title cursor"
                        onClick={() => goToProduct(item?.merchandise.product.handle)}
                      >
                        {item?.merchandise?.product?.title}
                      </h6>
                      {!!item?.merchandise?.selectedOptions?.length &&
                        item.merchandise.selectedOptions.map((item, itemKey) => {
                          if (item.name === 'Title') {
                            return null;
                          }
                          return (
                            <div key={itemKey} className="variation">
                              {item?.name}: {item?.value}
                            </div>
                          );
                        })}
                    </div>
                    <div className="cart-list-item-quantity">
                      <Quantity
                        currentQuantity={item?.quantity}
                        maxQuantity={item?.merchandise?.quantityAvailable as number}
                        disableCheckout={value => setDisableCheckout(value)}
                        onChange={(updatedCartLines, updatedCartCost, oldValue, newValue) => {
                          setCartLines(updatedCartLines);
                          setCartCost(updatedCartCost);
                          elevarHandleChange(item, oldValue, newValue);
                        }}
                        itemId={item.id}
                      />
                      <div className="cart-list-item-remove">
                        <Button
                          onClick={() => {
                            id && item?.id && removeItem(id, item);
                          }}
                          variation={ButtonVariation.LINK}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                    {item?.merchandise?.price?.amount && (
                      <div className="cart-list-item-price-wrapper">
                        <div className="cart-list-item-price">
                          {formatCurrency(+item.merchandise.price.amount)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {cartLoaded && !cartLines?.length && <p>Cart is empty</p>}

      <div className="cart-bottom">
        {cartLoaded && !!cartLines?.length && (
          <>
            <DiscountCode />
            <div className="cart-sum">
              <div className="cart-sum-row">
                <span>Subtotal</span>
                <span>
                  {cartCost?.subtotalAmount?.amount &&
                    formatCurrency(+cartCost?.subtotalAmount?.amount)}
                </span>
              </div>
              <div className="cart-sum-row highlight">
                <span>Total</span>
                <span>
                  {cartCost?.totalAmount?.amount && formatCurrency(+cartCost?.totalAmount?.amount)}
                </span>
              </div>
            </div>

            <CartBottom disableCheckout={disableCheckout} />
          </>
        )}
      </div>
    </div>
  );
};

export default CartContent;
